import './copy/copy.css';
import { viewerStrings } from './copy/strings.js';
import { message, hasText, optionalText, CopyError } from './copy/text.js';
import { createRoot } from 'react-dom/client';
import { lazy, Suspense, useEffect, useState, useSyncExternalStore } from 'react';
import { I18nProvider } from '@cloudscape-design/components/i18n';
import messages from '@cloudscape-design/components/i18n/messages/all.en';
import '@cloudscape-design/global-styles/index.css';
import Alert from '@cloudscape-design/components/alert';
import { WorkerClient } from './runtime/worker-client.js';
import { importWorkspace } from './state/import-workspace.js';
import { ImportController } from './state/import-controller.js';
import { RecordTable } from './components/RecordTable.js';
import { RetainedView } from './components/RetainedView.js';
import { WorkspaceLayout } from './components/WorkspaceLayout.js';
import { SpreadsheetView } from './components/SpreadsheetView.js';
import { Landing } from './components/Landing.js';
import { ReportDrilldown } from './components/ReportDrilldown.js';
import { RelationshipDrilldown } from './components/RelationshipDrilldown.js';
import { ExportDialog } from './components/ExportDialog.js';
import { RulesDialog } from './components/RulesDialog.js';
import { PropertyFilters } from './components/PropertyFilters.js';
import { FieldsDialog } from './components/FieldsDialog.js';
import { readFieldPreset, saveFieldPreset } from './state/field-preset-preference.js';
import { themes, getTheme } from './themes/catalog.js';
import { readAppearance, saveAppearance } from './themes/preferences.js';
import { readRecordView, saveRecordView, type RecordView } from './state/record-view-preference.js';
import './styles.css';
import type { ExecuteQuery } from './state/record-page.js';
import type { FindRelated } from './state/related-records.js';
import type {
  FieldValuesQuery,
  GroupReference,
  RelationshipGroupReference,
  RelationshipQueryDefinition,
  RelationshipValuesQuery,
} from '@cdr-clarity/contracts';

const ChartView = lazy(() =>
  import('./charts/ChartView.js').then((module) => ({ default: module.ChartView })),
);

const client = new WorkerClient(
  new Worker(new URL('./analysis.worker.js', import.meta.url), { type: 'module' }),
);
const execute: ExecuteQuery = (query) => client.request({ operation: 'query', query });
const findRelated: FindRelated = (query) => client.request({ operation: 'related', query });
const values = (query: FieldValuesQuery) => client.request({ operation: 'values', query });
const relationshipValues = (query: RelationshipValuesQuery) =>
  client.request({ operation: 'relationshipValues', query });
const executeRelationships = (query: RelationshipQueryDefinition) =>
  client.request({ operation: 'relationshipQuery', query });
const drillDown = (reference: GroupReference) =>
  client.request({ operation: 'drillDown', reference });
const relationshipDrillDown = (reference: RelationshipGroupReference) =>
  client.request({ operation: 'relationshipDrillDown', reference });

function App() {
  const [group, setGroup] = useState<GroupReference | null>(null);
  const [relationshipGroup, setRelationshipGroup] = useState<RelationshipGroupReference | null>(
    null,
  );
  const [section, setSection] = useState<'records' | 'charts'>(() =>
    location.hash === '#charts' ? 'charts' : 'records',
  );
  const [recordView, setRecordViewState] = useState<RecordView>(() => readRecordView(localStorage));
  const setRecordView = (view: RecordView) => {
    setRecordViewState(view);
    saveRecordView(localStorage, view);
  };
  const [fieldPreset, setFieldPresetState] = useState<string>(() => readFieldPreset(localStorage));
  const setFieldPreset = (id: string) => {
    setFieldPresetState(id);
    saveFieldPreset(localStorage, id);
  };
  const [fieldsOpen, setFieldsOpen] = useState(false);
  useEffect(() => {
    const navigate = () => setSection(location.hash === '#charts' ? 'charts' : 'records');
    window.addEventListener('hashchange', navigate);
    return () => window.removeEventListener('hashchange', navigate);
  }, []);
  const [imports] = useState(() => new ImportController(client, localStorage, importWorkspace));
  const importState = useSyncExternalStore(imports.subscribe, imports.getSnapshot);
  const loading = importState.busy;
  // Unmount all old workspace controls while revision-changing work is in flight.
  const workspace = loading ? null : importState.workspace;
  useEffect(() => {
    setGroup(null);
    setRelationshipGroup(null);
  }, [workspace]);
  const [initialAppearance] = useState(() => {
    try {
      return { theme: readAppearance(localStorage).theme, error: '' };
    } catch (reason) {
      return {
        theme: null,
        error: message(viewerStrings.webMain.savedAppearanceCouldNotBeLoaded, {
          value1: String(reason),
        }),
      };
    }
  });
  const [themeId, setThemeId] = useState(initialAppearance.theme);
  const [localError, setError] = useState(initialAppearance.error);
  const error = [localError, importState.error].filter(Boolean).join('\n');
  const theme = themeId === null ? null : getTheme(themeId);
  useEffect(() => {
    if (!theme) return;
    for (const key of [
      'color',
      'page',
      'panel',
      'ink',
      'muted',
      'line',
      'tint',
      'nav',
      'navInk',
    ] as const)
      document.documentElement.style.setProperty(`--lab-${key}`, theme[key]);
    document.body.style.fontFamily = theme.font;
    document.documentElement.style.setProperty('--lab-font', theme.font);
    document.documentElement.style.setProperty('--lab-headingFont', theme.headingFont);
  }, [theme]);
  useEffect(() => () => imports.dispose(), [imports]);
  if (!theme)
    return (
      <main className="app">
        <Alert type="error" header={optionalText(viewerStrings.webMain.savedAppearanceIsInvalid)}>
          {error}
        </Alert>
        {hasText(viewerStrings.webMain.resetAppearance) ? (
          <button
            type="button"
            onClick={() => {
              try {
                saveAppearance(localStorage, { version: 1, theme: 4 });
                setThemeId(1);
                setError('');
              } catch (reason) {
                setError(String(reason));
              }
            }}
          >
            {viewerStrings.webMain.resetAppearance}
          </button>
        ) : null}
      </main>
    );
  async function runImport(action: () => Promise<void>): Promise<void> {
    setError('');
    try {
      await action();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    }
  }
  const load = (files: File[]) => runImport(() => imports.load(files));
  const spreadsheet = !!workspace && section === 'records' && recordView === 'spreadsheet';
  const uploadButton = (
    <label className="upload-button" data-copy-label>
      {hasText(
        importState.accepted
          ? viewerStrings.webMain.addFiles
          : viewerStrings.webMain.openCSVCDRCMRFiles,
      ) ? (
        <span>
          {importState.accepted
            ? viewerStrings.webMain.addFiles
            : viewerStrings.webMain.openCSVCDRCMRFiles}
        </span>
      ) : null}
      <input
        aria-label={optionalText(
          importState.accepted
            ? viewerStrings.webAccessibility.main1addFiles
            : viewerStrings.webAccessibility.main1openCSVCDRCMRFiles,
        )}
        type="file"
        multiple
        disabled={loading}
        onChange={(event) => {
          if (event.target.files) void load([...event.target.files]);
          event.target.value = '';
        }}
      />
    </label>
  );
  const workspaceTools = (
    <section
      className="workspace-tools"
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        if (!loading) void load([...event.dataTransfer.files]);
      }}
    >
      {uploadButton}
      {!importState.accepted &&
        (hasText(viewerStrings.webMain.filesStayInThisBrowserNothingIs) ? (
          <span className="privacy">{viewerStrings.webMain.filesStayInThisBrowserNothingIs}</span>
        ) : null)}
      {workspace && (
        <>
          {section === 'records' && recordView === 'table' && (
            <>
              {hasText(viewerStrings.webMain.hideSelected) ? (
                <button type="button" onClick={() => workspace.records.hideSelected()}>
                  {viewerStrings.webMain.hideSelected}
                </button>
              ) : null}
              {hasText(viewerStrings.webMain.showHidden) ? (
                <button type="button" onClick={() => workspace.records.showHidden()}>
                  {viewerStrings.webMain.showHidden}
                </button>
              ) : null}
              <ExportDialog store={workspace.unified} execute={executeRelationships} />
            </>
          )}
          <RulesDialog state={workspace.rules} />
          {hasText(viewerStrings.webMain.callsMediaObservations) ? (
            <span className="record-count">
              {message(viewerStrings.webMain.callsMediaObservations, {
                value1: workspace.descriptor.effectiveCdrRecords.toLocaleString(),
                value2: ' ',
                value3: workspace.descriptor.effectiveCmrRecords.toLocaleString(),
              })}
            </span>
          ) : null}
        </>
      )}
      {loading && (
        <>
          {hasText(
            importState.stage === 'setup'
              ? viewerStrings.webMain.preparingWorkspace
              : importState.progress
                ? message(viewerStrings.webMain.recordsRead, {
                    value1: importState.progress.sourceRows.toLocaleString(),
                  })
                : viewerStrings.webMain.openingFiles,
          ) ? (
            <span role="status">
              {importState.stage === 'setup'
                ? viewerStrings.webMain.preparingWorkspace
                : importState.progress
                  ? message(viewerStrings.webMain.recordsRead, {
                      value1: importState.progress.sourceRows.toLocaleString(),
                    })
                  : viewerStrings.webMain.openingFiles}
            </span>
          ) : null}
          {hasText(viewerStrings.webMain.cancel) ? (
            <button type="button" onClick={() => client.cancel()}>
              {viewerStrings.webMain.cancel}
            </button>
          ) : null}
        </>
      )}
    </section>
  );
  return (
    <main
      className={`app${importState.accepted ? ' has-workspace' : ''}${spreadsheet ? ' spreadsheet-app' : ''}`}
    >
      <header className="app-bar">
        {(hasText(viewerStrings.webMain.cDR) || hasText(viewerStrings.webMain.clarity)) && (
          <a className="brand" href="/tools/cdr-clarity/">
            {viewerStrings.webMain.cDR}
            {hasText(viewerStrings.webMain.clarity) ? (
              <span>{viewerStrings.webMain.clarity}</span>
            ) : null}
          </a>
        )}
        {hasText(viewerStrings.webMain.byCallRecordingCom) ? (
          <a className="attribution" href="https://call-recording.com/">
            {viewerStrings.webMain.byCallRecordingCom}
          </a>
        ) : null}
        {workspace && (
          <div
            className="record-view-switch"
            data-copy-group
            aria-label={optionalText(viewerStrings.webMain.recordView)}
          >
            {hasText(viewerStrings.webMain.table) ? (
              <button
                aria-pressed={recordView === 'table'}
                onClick={() => {
                  setRecordView('table');
                  setSection('records');
                  location.hash = 'records';
                }}
              >
                {viewerStrings.webMain.table}
              </button>
            ) : null}
            {hasText(viewerStrings.webMain.excelLike) ? (
              <button
                aria-pressed={recordView === 'spreadsheet'}
                onClick={() => {
                  setRecordView('spreadsheet');
                  setSection('records');
                  location.hash = 'records';
                }}
              >
                {viewerStrings.webMain.excelLike}
              </button>
            ) : null}
          </div>
        )}
        {!spreadsheet && (
          <select
            aria-label={optionalText(viewerStrings.webMain.appearance)}
            value={theme.id}
            onChange={(event) => {
              const theme = Number(event.target.value);
              setThemeId(theme);
              try {
                saveAppearance(localStorage, { version: 1, theme });
              } catch (reason) {
                setError(
                  message(viewerStrings.webMain.appearanceChangedButCouldNotBeSaved, {
                    value1: String(reason),
                  }),
                );
              }
            }}
          >
            {[...themes]
              .sort((a, b) => a.name.localeCompare(b.name))
              .filter((design) => hasText(design.name))
              .map((design) => (
                <option key={design.id} value={design.id}>
                  {design.name}
                </option>
              ))}
          </select>
        )}
        {spreadsheet && workspaceTools}
      </header>
      {!importState.accepted && (
        <Landing
          upload={uploadButton}
          onDropFiles={(files) => void load(files)}
          view={recordView}
          onViewChange={setRecordView}
          preset={fieldPreset}
          onPresetChange={setFieldPreset}
        />
      )}
      {!spreadsheet && (importState.accepted || loading) && workspaceTools}
      {hasText(error) && (
        <Alert
          type="error"
          header={optionalText(viewerStrings.webMain.localAnalysisCouldNotFinish)}
        >
          {error}
        </Alert>
      )}
      {!loading && importState.accepted && importState.stage === 'setup' && importState.error && (
        <div className="field-tools" data-copy-group>
          {hasText(viewerStrings.webMain.retryWorkspaceSetup) ? (
            <button type="button" onClick={() => void runImport(() => imports.retrySetup())}>
              {viewerStrings.webMain.retryWorkspaceSetup}
            </button>
          ) : null}
          {importState.corruptPreferenceKey &&
            (hasText(viewerStrings.webMain.resetInvalidViewPreferences) ? (
              <button
                type="button"
                onClick={() => void runImport(() => imports.resetCorruptPreference())}
              >
                {viewerStrings.webMain.resetInvalidViewPreferences}
              </button>
            ) : null)}
        </div>
      )}
      {workspace && (
        <>
          <RetainedView active={spreadsheet}>
            <SpreadsheetView
              records={workspace.records}
              unified={workspace.unified}
              client={client}
            />
          </RetainedView>
          <div hidden={spreadsheet}>
            <WorkspaceLayout
              theme={theme}
              fields={workspace.fields}
              section={section}
              navigate={(next) => {
                setSection(next);
                location.hash = next;
              }}
            >
              {section === 'charts' && (
                <Suspense
                  fallback={
                    hasText(viewerStrings.webMain.loadingChartBuilder) ? (
                      <p role="status">{viewerStrings.webMain.loadingChartBuilder}</p>
                    ) : null
                  }
                >
                  <ChartView
                    store={workspace.records}
                    execute={execute}
                    values={values}
                    dark={theme.dark}
                    onGroup={(reference) => {
                      setRelationshipGroup(null);
                      setGroup(reference);
                    }}
                    fieldsAction={null}
                    relationship={{
                      store: workspace.unified,
                      execute: executeRelationships,
                      values: relationshipValues,
                      onGroup: (reference) => {
                        setGroup(null);
                        setRelationshipGroup(reference);
                      },
                    }}
                  />
                  {group && (
                    <ReportDrilldown
                      key={JSON.stringify(group)}
                      reference={group}
                      store={workspace.records}
                      drillDown={drillDown}
                      close={() => setGroup(null)}
                    />
                  )}
                  {relationshipGroup && (
                    <RelationshipDrilldown
                      key={JSON.stringify(relationshipGroup)}
                      reference={relationshipGroup}
                      store={workspace.unified}
                      drillDown={relationshipDrillDown}
                      close={() => setRelationshipGroup(null)}
                    />
                  )}
                </Suspense>
              )}
              <RetainedView active={section === 'records' && recordView === 'table'}>
                <PropertyFilters
                  store={workspace.unified}
                  values={relationshipValues}
                  leading={
                    <FieldsDialog
                      state={workspace.fields}
                      open={fieldsOpen}
                      onOpenChange={setFieldsOpen}
                      trigger="columns"
                    />
                  }
                />
                <RecordTable
                  active={section === 'records' && recordView === 'table'}
                  store={workspace.unified}
                  design={theme}
                  findRelated={findRelated}
                  execute={execute}
                  executeRelationships={executeRelationships}
                  onOpenFields={() => setFieldsOpen(true)}
                />
              </RetainedView>
            </WorkspaceLayout>
          </div>
        </>
      )}
      {!importState.accepted && (
        <footer data-copy-group>
          {hasText(viewerStrings.webMain.browserLocalAnalysisCiscoCUCMCDRAnd) && (
            <span>{viewerStrings.webMain.browserLocalAnalysisCiscoCUCMCDRAnd}</span>
          )}{' '}
          {hasText(viewerStrings.webMain.callRecordingCom) ? (
            <a href="https://call-recording.com/">{viewerStrings.webMain.callRecordingCom}</a>
          ) : null}
        </footer>
      )}
    </main>
  );
}

const host = document.getElementById('app');
if (!host) throw new CopyError(viewerStrings.webMain.applicationMountElementIsMissing);
createRoot(host).render(
  <I18nProvider locale="en" messages={[messages]}>
    <App />
  </I18nProvider>,
);
